import React, { FC, Fragment, useState } from 'react';
import { Alert, Box, Button, Divider, FormControlLabel, Radio, RadioGroup, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeliveryDatePicker from 'components/shared/delivery-date-picker/delivery-date-picker.component';
import { useSubcontractOrder } from 'pages/order-flow/subcontract-order/subcontract-order-context';
import {
    parentDentistName,
    parentHasIdentityNumber,
} from 'components/order-flow/subcontract-order/subcontract-input-helper';
import { formatPatientId, formatPatientName } from 'utils/format/format-patient';
import SubcontractFilesTable, { SelectableFile } from './subcontract-files-table.component';
import InfoText from 'components/shared/info-text.component';
import { CONSTANTS } from 'config/constants';
import { hasNoOpeningDays, useDeliveryDatePrefill } from './use-delivery-date-prefill';
import AttachScannerCaseDialog from 'components/scanner-cases/shared/attach-scanner-case-dialog/attach-scanner-case-dialog.component';
import { ScannerCase } from 'models/scanner-case';
import { patientDisplayName } from 'utils/orders';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type ContentStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
};

/** A switch label that echoes the value that will travel while the switch is on. */
const EchoedLabel: FC<{ label: string; value: string; isOn: boolean; dataTest: string }> = ({
    label,
    value,
    isOn,
    dataTest,
}) => (
    <Box component="span" display="inline-flex" alignItems="baseline" columnGap={2} flexWrap="wrap">
        <span>{label}</span>
        {isOn && value ? (
            <Typography component="span" variant="body2" color="text.secondary" data-test={dataTest}>
                {value}
            </Typography>
        ) : undefined}
    </Box>
);

/**
 * DD-3484: decide what content travels, item by item. Content not selected here is
 * absent from the sub-order entirely — the server builds the sub-order from this
 * selection and never copies the rest. Comments are not offered here: the next step
 * shows the parent's communication and the lab writes its own message.
 */
const ContentStep: FC<ContentStepProps> = ({ onBackClick, onNextClick }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { parentOrder, subcontract, updateSubcontract, updateSelection, refreshParentOrder } = useSubcontractOrder();
    const { selection } = subcontract;
    const { showBoundary } = useDenthubErrorBoundary();
    const [isImportScanDialogOpen, setIsImportScanDialogOpen] = useState(false);
    const [importedScanHasNoFiles, setImportedScanHasNoFiles] = useState(false);
    useDeliveryDatePrefill();

    const isScannerCasesEnabled = customerSettings.getCustomerSetting(
        currentOrgVar().customerSettings,
        CONSTANTS.SETTINGS_NAMES.SCAN_CASES,
    );

    const selectableFiles: SelectableFile[] = [
        ...(parentOrder.filesUrl ?? []).map((file) => ({
            fileId: file._id,
            filename: file.filename,
            url: file.url,
            mimetype: file.mimetype,
            isScannerCaseFile: false,
            uploadedOn: file.created?.on,
            uploadedBy: file.created?.by?.fullName,
        })),
        ...(parentOrder.attachedScannerCaseFiles ?? []).flatMap((scannerCase) =>
            (scannerCase.processedFiles ?? []).map((file) => ({
                fileId: file._id,
                filename: file.filename,
                url: file.url,
                mimetype: file.mimetype,
                isScannerCaseFile: true,
                displayCaseId: scannerCase.displayCaseId,
            })),
        ),
    ];

    const toggleFile = (file: SelectableFile) => {
        const key = file.isScannerCaseFile ? 'scannerCaseFileIds' : 'fileIds';
        const selectedIds = selection[key];
        updateSelection({
            [key]: selectedIds.includes(file.fileId)
                ? selectedIds.filter((selectedId) => selectedId !== file.fileId)
                : [...selectedIds, file.fileId],
        });
    };

    const isFileSelected = (file: SelectableFile) =>
        (file.isScannerCaseFile ? selection.scannerCaseFileIds : selection.fileIds).includes(file.fileId);

    const toggleAllFiles = (select: boolean) => {
        const idsOf = (isScannerCaseFile: boolean) =>
            select
                ? selectableFiles
                      .filter((file) => file.isScannerCaseFile === isScannerCaseFile)
                      .map((file) => file.fileId)
                : [];
        updateSelection({ fileIds: idsOf(false), scannerCaseFileIds: idsOf(true) });
    };

    /**
     * DD-3771: the picker attaches the scanner case to the parent order (where the main
     * lab needs it too), then its files are offered here like any other scan, pre-selected.
     */
    const closeImportScanDialog = async (scannerCase?: ScannerCase) => {
        setIsImportScanDialogOpen(false);
        if (!scannerCase) return;
        try {
            const refreshedParent = await refreshParentOrder();
            const importedFileIds = (refreshedParent.attachedScannerCaseFiles ?? [])
                .filter((attachedCase) => attachedCase?._id === scannerCase._id)
                .flatMap((attachedCase) => (attachedCase.processedFiles ?? []).map((file) => file._id));
            setImportedScanHasNoFiles(!importedFileIds.length);
            updateSelection({
                scannerCaseFileIds: [
                    ...selection.scannerCaseFileIds,
                    ...importedFileIds.filter((fileId) => !selection.scannerCaseFileIds.includes(fileId)),
                ],
            });
        } catch (error) {
            showBoundary(error);
        }
    };

    const dentistName = parentDentistName(parentOrder);

    return (
        <Fragment>
            <Box mb={2}>
                <Typography variant="h1">{translate('orders:SUBCONTRACT_CONTENT')}</Typography>
                <InfoText text={translate('orders:SUBCONTRACT_CONTENT_INFO')} />
            </Box>

            <Box mb={2}>
                <Typography variant="h4">{translate('common:FILES')}</Typography>
                {selectableFiles.length ? (
                    <SubcontractFilesTable
                        files={selectableFiles}
                        isSelected={isFileSelected}
                        onToggle={toggleFile}
                        onToggleAll={toggleAllFiles}
                    />
                ) : (
                    <InfoText text={translate('orders:SUBCONTRACT_NO_FILES')} />
                )}
                {isScannerCasesEnabled ? (
                    <Box mt={1}>
                        <Button
                            onClick={() => setIsImportScanDialogOpen(true)}
                            data-test="content-step.import-scanner-case-btn"
                        >
                            {translate('orders:SUBCONTRACT_IMPORT_SCANNER_CASE')}
                        </Button>
                        <InfoText text={translate('orders:SUBCONTRACT_IMPORT_SCANNER_CASE_INFO')} />
                    </Box>
                ) : undefined}
                {importedScanHasNoFiles ? (
                    <Alert severity="info" sx={{ mt: 1 }} data-test="content-step.imported-scan-has-no-files">
                        {translate('orders:SUBCONTRACT_IMPORTED_SCAN_HAS_NO_FILES')}
                    </Alert>
                ) : undefined}
                {/* PO decision (DD-3484): scan data may travel, but it can embed the
                    customer's and the patient's details, whatever the switches below say. */}
                {selection.scannerCaseFileIds.length ? (
                    <Alert severity="warning" sx={{ mt: 1 }} data-test="content-step.scan-case-warning">
                        {translate('orders:SUBCONTRACT_SCAN_CASE_DATA_WARNING')}
                    </Alert>
                ) : undefined}
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box mb={2}>
                <Typography variant="h4">{translate('orders:PATIENT')}</Typography>
                {parentOrder.patient ? (
                    <Box display="flex" flexDirection="column">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={selection.includePatientReference}
                                    onChange={(_event, checked) =>
                                        updateSelection({
                                            includePatientReference: checked,
                                            // The identity number never travels without the name.
                                            ...(checked ? {} : { includePatientIdentityNumber: false }),
                                        })
                                    }
                                    data-test="content-step.include-patient"
                                />
                            }
                            label={
                                <EchoedLabel
                                    label={translate('orders:SUBCONTRACT_INCLUDE_PATIENT')}
                                    value={formatPatientName(parentOrder.patient, '')}
                                    isOn={selection.includePatientReference}
                                    dataTest="content-step.patient-name-echo"
                                />
                            }
                        />
                        {parentHasIdentityNumber(parentOrder) ? (
                            <FormControlLabel
                                sx={{ ml: 4 }}
                                control={
                                    <Switch
                                        checked={selection.includePatientIdentityNumber}
                                        disabled={!selection.includePatientReference}
                                        onChange={(_event, checked) =>
                                            updateSelection({ includePatientIdentityNumber: checked })
                                        }
                                        data-test="content-step.include-patient-identity-number"
                                    />
                                }
                                label={
                                    <EchoedLabel
                                        label={translate('orders:SUBCONTRACT_INCLUDE_PATIENT_IDENTITY_NUMBER')}
                                        value={formatPatientId(parentOrder.patient)}
                                        isOn={
                                            selection.includePatientReference && selection.includePatientIdentityNumber
                                        }
                                        dataTest="content-step.patient-identity-number-echo"
                                    />
                                }
                            />
                        ) : undefined}
                    </Box>
                ) : (
                    <Alert severity="info">{translate('orders:SUBCONTRACT_PARENT_HAS_NO_PATIENT')}</Alert>
                )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box mb={2}>
                <Typography variant="h4">{translate('orders:DENTIST')}</Typography>
                {dentistName ? (
                    <Fragment>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={selection.includeDentistName}
                                    onChange={(_event, checked) => updateSelection({ includeDentistName: checked })}
                                    data-test="content-step.include-dentist-name"
                                />
                            }
                            label={translate('orders:SUBCONTRACT_INCLUDE_DENTIST_NAME', { name: dentistName })}
                        />
                        <InfoText text={translate('orders:SUBCONTRACT_DENTIST_NAME_INFO')} />
                    </Fragment>
                ) : (
                    <InfoText text={translate('orders:SUBCONTRACT_PARENT_HAS_NO_DENTIST')} />
                )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* DD-3770: pre-filled from the parent; the lab may change it for the sub-order. */}
            <Box mb={2}>
                <Typography variant="h4">{translate('orders:HOW_WAS_THE_SAMPLE_TAKEN')}</Typography>
                <RadioGroup
                    row
                    name="subcontract-sample-type"
                    value={subcontract.sampleType}
                    onChange={(event) => updateSubcontract({ sampleType: event.target.value })}
                >
                    {CONSTANTS.SAMPLE_METHODS.map((sampleMethod) => (
                        <FormControlLabel
                            key={sampleMethod}
                            value={sampleMethod}
                            control={<Radio color="secondary" data-test={`content-step.sample-type-${sampleMethod}`} />}
                            label={translate(`orders:${sampleMethod}`)}
                        />
                    ))}
                </RadioGroup>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box mb={2} maxWidth={400}>
                <Typography variant="h4" mb={1}>
                    {translate('orders:DELIVERY_DATE')}
                </Typography>
                <DeliveryDatePicker
                    date={subcontract.deliveryDate}
                    hour={subcontract.deliveryTime.hour}
                    minute={subcontract.deliveryTime.minute}
                    lab={subcontract.supplier}
                    dataTest="content-step.delivery-date"
                    allowTyping
                    onChange={(date, hour, minute) =>
                        updateSubcontract({
                            deliveryDate: date,
                            deliveryTime: { hour, minute },
                            isDeliveryDateFromDeadline:
                                subcontract.isDeliveryDateFromDeadline &&
                                Boolean(date) &&
                                date?.toDateString() === subcontract.deliveryDate?.toDateString(),
                        })
                    }
                />
                {subcontract.isDeliveryDateFromDeadline ? (
                    <InfoText text={translate('orders:SUBCONTRACT_DELIVERY_DATE_FROM_DEADLINE')} />
                ) : undefined}
                {/* DD-3689: the picker offers only the subcontractor's open days, so with none
                    set up it shows no dates at all. */}
                {hasNoOpeningDays(subcontract.supplier) ? (
                    <Alert severity="warning" sx={{ mt: 1 }} data-test="content-step.no-opening-days">
                        {translate('orders:SUBCONTRACT_SUPPLIER_HAS_NO_OPENING_DAYS', {
                            name: subcontract.supplier?.name,
                        })}
                    </Alert>
                ) : undefined}
            </Box>

            <Box display="flex" justifyContent="space-between" mt={2}>
                <Button onClick={onBackClick} data-test="content-step.back-btn">
                    {translate('common:BACK')}
                </Button>
                <Button
                    variant="contained"
                    disabled={!subcontract.deliveryDate}
                    onClick={onNextClick}
                    data-test="content-step.next-btn"
                >
                    {translate('orders:NEXT_STEP')}
                </Button>
            </Box>
            {isScannerCasesEnabled ? (
                <AttachScannerCaseDialog
                    orderId={parentOrder._id}
                    pin={formatPatientId(parentOrder.patient)}
                    name={patientDisplayName(parentOrder.patient)}
                    open={isImportScanDialogOpen}
                    onClose={closeImportScanDialog}
                />
            ) : undefined}
        </Fragment>
    );
};

export default ContentStep;
